import { useNavigate } from 'react-router';
import { KpiCard, Card } from '@/components';
import { useApi } from '@/api/hooks';
import styles from './Dashboard.module.css';

interface Stats {
  totalTenants: number;
  activeTenants: number;
  mrr: number;
  activeUsers: number;
  churnRate: number;
  newTenantsThisMonth: number;
  prevMrr: number;
  prevActiveUsers: number;
  prevChurnRate: number;
}

interface Tenant {
  id: string;
  company_name: string;
  status: string;
  plan_name: string;
  price_monthly: number;
  created_at: string;
}

interface MrrMonth { month: string; mrr: number; }
interface BillingItem { status: string; amount: number; company_name: string; }
interface Ticket { status: string; }
interface RecentEvent { type: string; company_name: string; description: string; created_at: string; }

const eventColorMap: Record<string, string> = {
  tenant_created: 'var(--green)',
  plan_upgrade: 'var(--brand)',
  billing_unpaid: 'var(--amber)',
  cancelling: 'var(--red)',
  support_ticket: 'var(--purple)',
  system: 'var(--ink3)',
};

export function Dashboard() {
  const navigate = useNavigate();
  const { data: stats, loading: statsLoading } = useApi<Stats>('/console/stats');
  const { data: tenants } = useApi<Tenant[]>('/console/tenants');
  const { data: mrrHistory } = useApi<MrrMonth[]>('/console/mrr-history');
  const { data: billing } = useApi<BillingItem[]>('/console/billing');
  const { data: tickets } = useApi<Ticket[]>('/console/tickets');
  const { data: recentEvents } = useApi<RecentEvent[]>('/console/recent-events');
  const { data: monStats } = useApi<{ uptimePercent: number }>('/console/monitoring/stats');

  const mrrChangePercent = stats && stats.prevMrr > 0
    ? Math.round(((stats.mrr - stats.prevMrr) / stats.prevMrr) * 100)
    : 0;

  const kpis = stats ? [
    { label: '総テナント数', value: String(stats.totalTenants), delta: `↑ 今月 +${stats.newTenantsThisMonth}社`, trend: 'up' as const },
    { label: 'MRR', value: `¥${stats.mrr.toLocaleString()}`, delta: mrrChangePercent >= 0 ? `↑ +${mrrChangePercent}% MoM` : `↓ ${mrrChangePercent}% MoM`, trend: (mrrChangePercent >= 0 ? 'up' : 'down') as 'up' | 'down' },
    { label: 'アクティブユーザー', value: String(stats.activeUsers), delta: (() => { const diff = stats.activeUsers - stats.prevActiveUsers; return diff > 0 ? `↑ 前月比 +${diff}` : diff < 0 ? `↓ 前月比 ${diff}` : '前月と同じ'; })(), trend: (stats.activeUsers >= stats.prevActiveUsers ? 'up' : 'down') as 'up' | 'down' },
    { label: 'チャーン率', value: `${stats.churnRate}%`, delta: `↓ 先月 ${stats.prevChurnRate}%`, trend: 'down' as const },
  ] : [];

  // プラン別内訳
  const planBreakdown = (tenants ?? []).reduce((acc, t) => {
    if (!acc[t.plan_name]) acc[t.plan_name] = { count: 0, revenue: 0 };
    acc[t.plan_name].count += 1;
    acc[t.plan_name].revenue += (t.price_monthly ?? 0);
    return acc;
  }, {} as Record<string, { count: number; revenue: number }>);
  const totalTenantCount = (tenants ?? []).length || 1;

  const planColors: Record<string, string> = { Standard: 'var(--brand)', Professional: 'var(--green)', Enterprise: 'var(--amber)' };

  // サマリーカード
  const activeTenantCount = (tenants ?? []).filter((t) => t.status === 'active' || t.status === 'trial').length;
  const suspendedCount = (tenants ?? []).filter((t) => t.status === 'suspended').length;
  const cancellingCount = (tenants ?? []).filter((t) => t.status === 'cancelling').length;
  const unpaidAmount = (billing ?? []).filter((b) => b.status === 'unpaid').reduce((s, b) => s + b.amount, 0);
  const unpaidTenants = [...new Set((billing ?? []).filter((b) => b.status === 'unpaid').map((b) => b.company_name))];
  const openTickets = (tickets ?? []).filter((t) => t.status === 'open' || t.status === 'in_progress').length;

  const events = (recentEvents ?? []).slice(0, 6);

  const maxMrr = Math.max(...(mrrHistory ?? []).map((x) => Number(x.mrr)), 1);

  if (statsLoading) return <div className={styles.page}><div className={styles.loading}>データ読込中...</div></div>;

  return (
    <div className={styles.page}>
      <div className={styles.kpiRow}>
        {kpis.map((k) => <KpiCard key={k.label} {...k} />)}
      </div>

      <div className={styles.grid2}>
        {/* 左: MRR推移 + プラン別内訳 */}
        <Card title="📈 MRR推移（6ヶ月）">
          {(mrrHistory ?? []).length > 0 ? (
            <>
              <div className={styles.chartArea}>
                {(mrrHistory ?? []).map((m, i) => {
                  const isLast = i === (mrrHistory ?? []).length - 1;
                  const pct = (Number(m.mrr) / maxMrr) * 100;
                  const val = Number(m.mrr);
                  const label = val >= 10000
                    ? `¥${(val / 10000).toFixed(val % 10000 === 0 ? 0 : 1)}万`
                    : `¥${val.toLocaleString()}`;
                  const prev = i > 0 ? Number((mrrHistory ?? [])[i - 1].mrr) : null;
                  const diff = prev !== null ? val - prev : null;
                  const diffStr = diff !== null
                    ? (diff >= 0 ? `前月比 +¥${diff.toLocaleString()}` : `前月比 -¥${Math.abs(diff).toLocaleString()}`)
                    : '';
                  return (
                    <div key={m.month} className={styles.barCol} title={`${m.month}：${label}${diffStr ? `（${diffStr}）` : ''}`}>
                      <span className={styles.barValue}>{label}</span>
                      <div
                        className={`${styles.bar} ${isLast ? styles.barCurrent : ''}`}
                        style={{ height: `${pct}%` }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className={styles.barLabels}>
                {(mrrHistory ?? []).map((m, i) => (
                  <span key={m.month} className={`${styles.barLabel} ${i === (mrrHistory ?? []).length - 1 ? styles.barLabelCurrent : ''}`}>
                    {m.month.slice(5)}月
                  </span>
                ))}
              </div>
            </>
          ) : <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink3)' }}>請求データなし</div>}
          <div className={styles.planBreakdown}>
            {Object.entries(planBreakdown).filter(([name]) => name !== 'Trial').map(([name, data]) => (
              <div key={name} className={styles.planRow}>
                <span className={styles.planName}>{name}（{data.count}社）</span>
                <div className={styles.planBar}>
                  <div className={styles.planFill} style={{ width: `${(data.count / totalTenantCount) * 100}%`, background: planColors[name] ?? 'var(--ink3)' }} />
                </div>
                <span className={styles.planVal}>¥{data.revenue.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* 右: 最新イベント */}
        <Card title="🔔 最新イベント">
          <div className={styles.eventList}>
            {events.length > 0 ? events.map((e, i) => (
              <div key={`${e.type}-${i}`} className={styles.eventItem} onClick={() => {
                if (e.type === 'billing_unpaid') navigate('/billing');
                else if (e.type === 'support_ticket') navigate('/support');
                else navigate('/tenants');
              }} style={{ cursor: 'pointer' }}>
                <div className={styles.eventDot} style={{ background: eventColorMap[e.type] ?? 'var(--ink3)' }} />
                <span className={styles.eventText}><strong>{e.company_name}</strong> {e.description}</span>
                <span className={styles.eventTime}>{new Date(e.created_at).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            )) : (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink3)' }}>イベントなし</div>
            )}
          </div>
        </Card>
      </div>

      {/* サマリーカード4列 */}
      <div className={styles.summaryRow}>
        <div className={styles.summaryCard} onClick={() => navigate('/tenants')}>
          <span className={styles.summaryIcon}>🏢</span>
          <div className={styles.summaryInfo}>
            <div className={styles.summaryLabel}>アクティブテナント</div>
            <div className={styles.summaryValue}>{activeTenantCount}</div>
            <div className={styles.summarySub} style={{ color: 'var(--amber)' }}>停止中 {suspendedCount} ｜ 解約申請 {cancellingCount}</div>
          </div>
        </div>
        <div className={styles.summaryCard} onClick={() => navigate('/billing')}>
          <span className={styles.summaryIcon}>💳</span>
          <div className={styles.summaryInfo}>
            <div className={styles.summaryLabel}>未収入金</div>
            <div className={styles.summaryValue} style={{ color: unpaidAmount > 0 ? 'var(--amber)' : undefined }}>¥{unpaidAmount.toLocaleString()}</div>
            <div className={styles.summarySub}>{unpaidTenants.length > 0 ? `${unpaidTenants[0]} ${unpaidTenants.length}社` : '—'}</div>
          </div>
        </div>
        <div className={styles.summaryCard} onClick={() => navigate('/monitoring')}>
          <span className={styles.summaryIcon}>📡</span>
          <div className={styles.summaryInfo}>
            <div className={styles.summaryLabel}>システム稼働率</div>
            <div className={styles.summaryValue} style={{ color: (monStats?.uptimePercent ?? 99.9) >= 99.9 ? 'var(--green)' : 'var(--amber)' }}>{monStats?.uptimePercent ?? 99.9}%</div>
            <div className={styles.summarySub}>直近30日間</div>
          </div>
        </div>
        <div className={styles.summaryCard} onClick={() => navigate('/support')}>
          <span className={styles.summaryIcon}>🎧</span>
          <div className={styles.summaryInfo}>
            <div className={styles.summaryLabel}>未対応チケット</div>
            <div className={styles.summaryValue} style={{ color: openTickets > 0 ? 'var(--red)' : undefined }}>{openTickets}</div>
            <div className={styles.summarySub}>高優先度 {(tickets ?? []).filter((t) => t.status === 'open').length}件</div>
          </div>
        </div>
      </div>
    </div>
  );
}
